import { parseAmount } from '../services/cost-math';
import { backfillScopeDescription, isUserOwnedScopeCopy, normaliseScopeCopyItem, resolveClientScopeDescription } from './scope-descriptions';
import { chooseAuthoritativeScopePhotoItem } from './scope-photo-authority';
import { hasReviewableManualAmountAuthority } from './quote-pricing-readiness';

// Single Scope Description: mirror it into the legacy `description` field
// (fill-if-empty) on the merge OUTPUT so the Stage 4↔5 merge keeps one
// description per item for downstream readers. Identity-preserving: returns the
// SAME array reference (and same item objects) when nothing needed backfilling,
// so callers that assert "no needless re-allocation" still hold.
const mirrorScopeDescriptions = (items) => {
    if (!Array.isArray(items)) return items;
    let changed = false;
    const mapped = items.map((it) => {
        const next = backfillScopeDescription(it);
        if (next !== it) changed = true;
        return next;
    });
    return changed ? mapped : items;
};

export const hasNonEmptyText = (value) => typeof value === 'string' && value.trim().length > 0;

const isPlaceholderTitle = (value) => /^new item$/i.test(String(value || '').trim());

const inclusionText = (inc) => {
    if (typeof inc === 'string') return inc.trim();
    if (inc && typeof inc === 'object') return String(inc.text || '').trim();
    return '';
};

export const hasMeaningfulInclusions = (items) => (
    Array.isArray(items) && items.some(inc => inclusionText(inc))
);

export const hasPlaceholderInclusions = (items) => (
    Array.isArray(items)
    && items.length === 1
    && /^inclusion\s*1$/i.test(inclusionText(items[0]))
);

export const clientDescriptionFromItem = (item) => resolveClientScopeDescription(item, { fallbackToTitle: false });

export const scopeCodeForPosition = (item, index) => {
    const isFrancisPreliminaries = item?.id === 'francis-preliminaries-administration'
        || item?.adminKind === 'preliminaries_and_administration'
        || item?.admin_kind === 'preliminaries_and_administration';
    if (isFrancisPreliminaries) return 'PRELIMS';
    if ((item?.systemManaged || item?.system_managed) && hasNonEmptyText(item?.code)) {
        return item.code;
    }
    return String(index + 1);
};

export const scopeDisplayCode = (item, index) => (
    (item?.systemManaged || item?.system_managed || item?.isAdminItem === true || item?.is_admin_item === true)
        ? String(index + 1)
        : (hasNonEmptyText(item?.code) ? item.code : String(index + 1))
);

const explicitClientDescriptionFromItem = (item) => (
    resolveClientScopeDescription(item, { fallbackToTitle: false })
);

export const scopeMergeKeys = (item) => {
    const keys = [];
    if (item?.id) keys.push(`id:${item.id}`);
    if (item?.activityRef?.activityId) keys.push(`activity:${item.activityRef.activityId}`);
    if (!item?.id && !item?.activityRef?.activityId && item?.code) keys.push(`code:${item.code}`);
    return keys;
};

const stampMs = (...values) => {
    for (const raw of values) {
        const ms = raw ? Date.parse(raw) : NaN;
        if (Number.isFinite(ms)) return ms;
    }
    return 0;
};

const pricingStampMs = (item) => {
    return stampMs(
        item?.pricingEditedAt,
        item?.pricing_edited_at,
        item?.amountEditedAt,
        item?.amount_edited_at,
        item?.priceEditedAt,
        item?.price_edited_at,
        item?.amountLockedAt,
        item?.amount_locked_at,
        item?.costBreakdownEditedAt,
        item?.cost_breakdown_edited_at,
    );
};

const shouldPreserveQuotePricing = (quoteItem, liveItem) => {
    const quoteAmount = parseAmount(quoteItem?.amount);
    const liveAmount = parseAmount(liveItem?.amount);
    if (!(quoteAmount > 0)) return false;
    if (!(liveAmount > 0)) return true;
    if (Math.abs(quoteAmount - liveAmount) <= 0.009) return false;
    const quoteHasManualAmount = hasReviewableManualAmountAuthority(quoteItem);
    const liveHasManualAmount = hasReviewableManualAmountAuthority(liveItem);
    if (liveHasManualAmount && !quoteHasManualAmount) return false;
    if (quoteHasManualAmount && !liveHasManualAmount) return true;
    const quoteEdited = pricingStampMs(quoteItem);
    const liveEdited = pricingStampMs(liveItem);
    if (liveEdited > quoteEdited) return false;
    if (quoteEdited > liveEdited) return true;
    // A deterministic Stage 4 repair/current-rate commit is a real pricing
    // receipt even when the legacy row predates field-specific timestamps.
    // An old generated Stage 5 snapshot must not win a 0 === 0 timestamp tie
    // and reintroduce the exact below-floor amount that was just repaired.
    if (!quoteHasManualAmount && (
        liveItem?.pricingFloorAutoAdjusted === true
        || liveItem?.amountAuthority === 'committed'
    )) return false;
    return !(liveEdited > quoteEdited);
};

const copyStampMs = (item) => {
    const explicitStamp = stampMs(
        item?.copyEditedAt,
        item?.descriptionEditedAt,
        item?.descriptionBriefEditedAt,
        item?.titleEditedAt,
        item?.inclusionsEditedAt,
        item?.notesToClientEditedAt,
        item?.notes_to_clientEditedAt,
    );
    if (explicitStamp) return explicitStamp;
    return item?.manuallyEdited === true
        ? stampMs(item?.humanEditedAt, item?.scopeEditedAt)
        : 0;
};

const hasClientCopy = (item) => (
    hasNonEmptyText(item?.title)
    || hasNonEmptyText(item?.description)
    || hasNonEmptyText(clientDescriptionFromItem(item))
    || hasMeaningfulInclusions(item?.inclusions)
    || hasNonEmptyText(item?.notes_to_client)
);

const shouldPreserveQuoteClientCopy = (quoteItem, liveItem) => {
    if (!hasClientCopy(quoteItem)) return false;
    const quoteEdited = copyStampMs(quoteItem);
    const liveEdited = copyStampMs(liveItem);
    // A newer client-copy edit is allowed to flow into the quote. Broad record
    // timestamps alone are not enough authority to replace Stage 5 copy.
    return !(liveEdited > quoteEdited && liveEdited > 0);
};

const isPreservableQuoteOnlyItem = (item) => (
    item?.quoteOnly === true
    || item?.finalQuoteOnly === true
    || item?.addedInStage5 === true
    || item?.source === 'stage5-manual'
    || (scopeMergeKeys(item).length === 0 && item?.manuallyEdited === true)
);

const scopeTopologyStampMs = (item) => Math.max(
    copyStampMs(item),
    pricingStampMs(item),
    stampMs(
        item?.humanEditedAt,
        item?.scopeEditedAt,
        item?.editedAt,
        item?.updatedAt,
    ),
);

const stage5BackSyncOwnsTopology = (quoteItems, liveItems, options) => {
    if (options?.intent !== 'stage5-back-sync') return false;
    if (options?.quoteScopeEditedBy !== 'user') return false;
    const quoteEditedAt = stampMs(options?.quoteScopeEditedAt);
    if (!(quoteEditedAt > 0)) return false;

    // Deletion/order authority is safe only against the exact topology the
    // Stage 5 editor opened. Timestamps cannot prove that an unstamped AI/WMS
    // row was not added concurrently. If live topology drifted by even one
    // row or position, fall back to the ordinary live-topology merge: builder
    // copy/pricing edits still reconcile, but no live row is silently erased.
    const baseRows = Array.isArray(options?.quoteScopeBaseKeys)
        ? options.quoteScopeBaseKeys
        : null;
    if (!baseRows || baseRows.length !== liveItems.length) return false;
    const exactBaseTopology = baseRows.every((baseKeys, index) => {
        if (!Array.isArray(baseKeys) || baseKeys.length === 0) return false;
        const liveKeys = new Set(scopeMergeKeys(liveItems[index]));
        return baseKeys.some((key) => liveKeys.has(key));
    });
    if (!exactBaseTopology) return false;

    // Exact topology proves no row was added/reordered after open, but an
    // opening row may still have been edited in Stage 4 while Stage 5 deleted
    // it. A deletion stamped at 04:00 must not erase a row whose copy/pricing
    // authority advanced at 05:00. This is deliberately ANDed with the base
    // receipt rather than used as a substitute for it: unstamped WMS additions
    // are protected by the topology receipt above.
    const quoteKeys = new Set(quoteItems.flatMap(scopeMergeKeys));
    const hasNewerUnmatchedLiveAuthority = liveItems.some((item) => (
        !scopeMergeKeys(item).some((key) => quoteKeys.has(key))
        && scopeTopologyStampMs(item) > quoteEditedAt
    ));
    return !hasNewerUnmatchedLiveAuthority;
};

const pickDefinedOwnFields = (item, fields) => fields.reduce((picked, field) => {
    if (Object.prototype.hasOwnProperty.call(item || {}, field) && item[field] !== undefined) {
        picked[field] = item[field];
    }
    return picked;
}, {});

const mirrorDefinedAliasPairs = (fields, item, pairs) => {
    pairs.forEach(([canonical, alias]) => {
        const hasCanonical = Object.prototype.hasOwnProperty.call(item || {}, canonical)
            && item[canonical] !== undefined;
        const hasAlias = Object.prototype.hasOwnProperty.call(item || {}, alias)
            && item[alias] !== undefined;
        if (!hasCanonical && !hasAlias) return;
        const value = hasCanonical ? item[canonical] : item[alias];
        fields[canonical] = value;
        fields[alias] = value;
    });
    return fields;
};

const scopeAmountAuthorityFields = (item) => {
    const fields = pickDefinedOwnFields(item, [
        'amount', 'estimated_amount', 'amountAuthority', 'amount_authority',
        'amountLocked', 'amount_locked', 'amountLockReason', 'amount_lock_reason',
        'amountEditedAt', 'amount_edited_at', 'amountEditedBy', 'amount_edited_by',
        'priceEditedAt', 'price_edited_at', 'priceEditedBy', 'price_edited_by',
        'amountLockedAt', 'amount_locked_at',
        'pricingBasis', 'pricing_basis', 'amountMode', 'amount_mode',
        'isGstInclusive', 'gstInclusive',
        'quoted_amount_inc_gst', 'quoted_amount_ex_gst',
    ]);
    if (fields.amount !== undefined && fields.estimated_amount === undefined) {
        fields.estimated_amount = fields.amount;
    }
    return mirrorDefinedAliasPairs(fields, item, [
        ['amountAuthority', 'amount_authority'],
        ['amountLocked', 'amount_locked'],
        ['amountLockReason', 'amount_lock_reason'],
        ['amountEditedAt', 'amount_edited_at'],
        ['amountEditedBy', 'amount_edited_by'],
        ['priceEditedAt', 'price_edited_at'],
        ['priceEditedBy', 'price_edited_by'],
        ['amountLockedAt', 'amount_locked_at'],
        ['pricingBasis', 'pricing_basis'],
        ['amountMode', 'amount_mode'],
        ['isGstInclusive', 'gstInclusive'],
    ]);
};

const quotePricingFields = (quoteItem) => {
    const fields = {
        ...scopeAmountAuthorityFields(quoteItem),
        ...pickDefinedOwnFields(quoteItem, [
        'costBreakdown', 'cost_breakdown', 'costBreakdownLocked', 'cost_breakdown_locked',
        'pricingEditedAt', 'pricing_edited_at', 'costBreakdownEditedAt', 'cost_breakdown_edited_at',
        'gstExempt', 'gst_exempt',
        'marginPercent', 'margin_percent', 'marginPctOverride', 'margin_pct_override',
        'provisionalSum', 'provisional_sum',
        'pricingDisplayAmount', 'pricing_display_amount',
        ]),
    };
    return mirrorDefinedAliasPairs(fields, quoteItem, [
        ['costBreakdown', 'cost_breakdown'],
        ['costBreakdownLocked', 'cost_breakdown_locked'],
        ['pricingEditedAt', 'pricing_edited_at'],
        ['costBreakdownEditedAt', 'cost_breakdown_edited_at'],
        ['gstExempt', 'gst_exempt'],
        ['marginPercent', 'margin_percent'],
        ['marginPctOverride', 'margin_pct_override'],
        ['provisionalSum', 'provisional_sum'],
        ['pricingDisplayAmount', 'pricing_display_amount'],
    ]);
};

const copyAuthorityFields = (item) => {
    const fields = [
        'title', 'description', 'descriptionBrief', 'description_brief', 'clientDescription',
        'inclusions', 'notes_to_client', 'images', 'manuallyEdited', 'humanEditedAt', 'editedBy',
        'copyEditedAt', 'descriptionEditedAt', 'descriptionBriefEditedAt', 'titleEditedAt',
        'inclusionsEditedAt', 'notesToClientEditedAt', 'notes_to_clientEditedAt',
    ];
    return fields.reduce((picked, field) => {
        if (Object.prototype.hasOwnProperty.call(item || {}, field) && item[field] !== undefined) picked[field] = item[field];
        return picked;
    }, {});
};

const normaliseManagedAdminIdentity = (item) => {
    if (!item || typeof item !== 'object') return item;
    const isFrancisPreliminaries = item.id === 'francis-preliminaries-administration'
        || item.adminKind === 'preliminaries_and_administration'
        || item.admin_kind === 'preliminaries_and_administration';
    if (!isFrancisPreliminaries || item.code === 'PRELIMS') return item;
    return { ...item, code: 'PRELIMS' };
};

// v5.31.2 fix: when amountLocked === true the user's edited item.amount is
// authoritative. Apply this as a post-merge normalisation step so it fires for
// BOTH the preserveQuotePricing path (where stale snapshots travel via
// quotePricingFields) AND the non-preserve path (where the merged item still
// inherits stale snapshots via the plain spread because quote+live both carry
// the same pre-edit snapshot). Same intent as the v5.31.1 fix at
// resolveScopePricing (READ-time staleness); this closes the WRITE-time side
// so merged scope items stop carrying a contradictory (amount vs
// quoted_amount_*_gst) pair into Stage 5 and any consumer that reads
// quoted_amount_*_gst directly. Mirrors inverse GST derivation in
// pricing-basis.js (gstExempt → no grossing).
const refreshQuotedAmountsFromLockedAmount = (item) => {
    if (!item || typeof item !== 'object') return item;
    const amountLocked = item.amountLocked === true || item.amount_locked === true;
    if (!amountLocked) return item;
    const rawAmount = item.amount;
    if (rawAmount == null || rawAmount === '') return item;
    const numericAmount = Number(String(rawAmount).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(numericAmount) || !(numericAmount > 0)) return item;
    const gstExempt = item.gstExempt === true || item.gst_exempt === true;
    const basisToken = String(item.pricingBasis || item.pricing_basis || item.amountMode || item.amount_mode || '')
        .trim().toLowerCase().replace(/[\s-]+/g, '_');
    const explicitlyExGst = basisToken === 'ex' || basisToken === 'ex_gst' || basisToken === 'exclusive'
        || basisToken === 'gst_exclusive' || basisToken === 'excluding_gst' || basisToken === 'excl' || basisToken === 'excl_gst'
        || item.isGstInclusive === false || item.gstInclusive === false;
    let fresh_inc_gst;
    let fresh_ex_gst;
    if (explicitlyExGst) {
        fresh_ex_gst = Math.round(numericAmount * 100) / 100;
        fresh_inc_gst = gstExempt ? fresh_ex_gst : Math.round(fresh_ex_gst * 110) / 100;
    } else {
        fresh_inc_gst = Math.round(numericAmount * 100) / 100;
        fresh_ex_gst = gstExempt ? fresh_inc_gst : Math.round((fresh_inc_gst / 1.1) * 100) / 100;
    }
    // Only mutate if the stored snapshot disagrees with the freshly-derived
    // pair — preserves object identity (and thus referential-equality memo
    // gates) when the snapshot is already aligned.
    if (item.quoted_amount_inc_gst === fresh_inc_gst && item.quoted_amount_ex_gst === fresh_ex_gst) {
        return item;
    }
    return { ...item, quoted_amount_inc_gst: fresh_inc_gst, quoted_amount_ex_gst: fresh_ex_gst };
};

const mergeMatchedQuoteScopeItem = (quoteItem, liveItem) => {
    const liveBrief = explicitClientDescriptionFromItem(liveItem);
    const quoteBrief = clientDescriptionFromItem(quoteItem);
    const liveHasInclusions = hasMeaningfulInclusions(liveItem?.inclusions) && !hasPlaceholderInclusions(liveItem?.inclusions);
    const quoteHasInclusions = hasMeaningfulInclusions(quoteItem?.inclusions);
    const preserveQuotePricing = shouldPreserveQuotePricing(quoteItem, liveItem);
    const preserveQuoteCopy = shouldPreserveQuoteClientCopy(quoteItem, liveItem);
    const authoritativePhotoItem = chooseAuthoritativeScopePhotoItem({ quoteItem, liveItem });

    const liveCopyWins = !preserveQuoteCopy || (isUserOwnedScopeCopy(liveItem) && !isUserOwnedScopeCopy(quoteItem));
    const resolvedDescriptionBrief = preserveQuoteCopy && hasNonEmptyText(quoteBrief)
        ? quoteBrief
        : (liveCopyWins
            ? liveBrief
            : (hasNonEmptyText(liveBrief) ? liveBrief : quoteBrief));
    const resolvedDescription = preserveQuoteCopy && hasNonEmptyText(quoteItem?.description)
        ? quoteItem.description
        : (liveCopyWins
            ? (liveItem?.description !== undefined ? liveItem.description : liveBrief)
            : (hasNonEmptyText(liveItem?.description) ? liveItem.description : quoteItem?.description));

    return refreshQuotedAmountsFromLockedAmount(normaliseScopeCopyItem(normaliseManagedAdminIdentity({
        ...quoteItem,
        ...liveItem,
        ...(preserveQuotePricing ? quotePricingFields(quoteItem) : {}),
        title: preserveQuoteCopy && hasNonEmptyText(quoteItem?.title)
            ? quoteItem.title
            : (!hasNonEmptyText(liveItem?.title) || isPlaceholderTitle(liveItem?.title)) && hasNonEmptyText(quoteItem?.title)
                ? quoteItem.title
                : liveItem.title,
        description: resolvedDescription,
        descriptionBrief: resolvedDescriptionBrief,
        description_brief: resolvedDescriptionBrief,
        clientDescription: resolvedDescriptionBrief,
        notes_to_client: preserveQuoteCopy && hasNonEmptyText(quoteItem?.notes_to_client)
            ? quoteItem.notes_to_client
            : (hasNonEmptyText(liveItem?.notes_to_client) ? liveItem.notes_to_client : quoteItem?.notes_to_client),
        inclusions: preserveQuoteCopy && quoteHasInclusions
            ? quoteItem.inclusions
            : (liveHasInclusions || !quoteHasInclusions ? liveItem?.inclusions : quoteItem.inclusions),
        images: Array.isArray(authoritativePhotoItem?.images) ? authoritativePhotoItem.images : [],
        photoAssignmentsEditedAt: authoritativePhotoItem?.photoAssignmentsEditedAt,
        photoAssignmentsEditedBy: authoritativePhotoItem?.photoAssignmentsEditedBy,
        photoAssignmentsLocked: authoritativePhotoItem?.photoAssignmentsLocked,
        photoAssignmentReviewReceipt: authoritativePhotoItem?.photoAssignmentReviewReceipt,
    })));
};

export function mergeQuoteScopeWithLiveScope(quoteScope = [], liveScope, options = {}) {
    const quoteItems = Array.isArray(quoteScope) ? quoteScope : [];
    const liveWasProvided = Array.isArray(liveScope);
    const liveItems = liveWasProvided ? liveScope : [];
    const quoteTopologyWins = liveWasProvided && stage5BackSyncOwnsTopology(quoteItems, liveItems, options);
    if (quoteTopologyWins) {
        const liveByKey = new Map();
        liveItems.forEach((item) => {
            scopeMergeKeys(item).forEach((key) => {
                if (!liveByKey.has(key)) liveByKey.set(key, item);
            });
        });
        return mirrorScopeDescriptions(quoteItems.map((quoteItem, index) => {
            const liveItem = scopeMergeKeys(quoteItem).map((key) => liveByKey.get(key)).find(Boolean) || null;
            const mergedItem = liveItem
                ? mergeMatchedQuoteScopeItem(quoteItem, liveItem)
                : normaliseScopeCopyItem(quoteItem);
            return {
                ...mergedItem,
                code: scopeCodeForPosition(mergedItem, index),
            };
        }));
    }
    if (quoteItems.length === 0) return liveItems;
    if (!liveWasProvided) return quoteItems;
    if (liveItems.length === 0) {
        return mirrorScopeDescriptions(quoteItems
            .filter(isPreservableQuoteOnlyItem)
            .map(item => normaliseScopeCopyItem(item)));
    }

    const quoteByKey = new Map();
    quoteItems.forEach((item) => {
        scopeMergeKeys(item).forEach(key => {
            if (!quoteByKey.has(key)) quoteByKey.set(key, item);
        });
    });

    const matchedQuoteItems = new Set();
    const merged = liveItems.map((liveItem) => {
        const quoteItem = scopeMergeKeys(liveItem).map(key => quoteByKey.get(key)).find(Boolean) || null;
        if (!quoteItem) return liveItem;
        matchedQuoteItems.add(quoteItem);
        return mergeMatchedQuoteScopeItem(quoteItem, liveItem);
    });

    // A live row that absorbed other rows in an Assistant merge names them in
    // mergedFromItemIds. The quote may still hold an absorbed quote-only row
    // (added in Stage 5); re-adding it would count its amount twice.
    const absorbedIds = new Set(liveItems.flatMap((item) => (
        Array.isArray(item?.mergedFromItemIds) ? item.mergedFromItemIds : []
    )));
    quoteItems.forEach((item) => {
        if (matchedQuoteItems.has(item) || !isPreservableQuoteOnlyItem(item)) return;
        if (item?.id && absorbedIds.has(item.id)) return;
        merged.push(normaliseScopeCopyItem(item));
    });
    return mirrorScopeDescriptions(merged);
}

// ── QW-FIX-2: edit-preserving WMS writeback merge ───────────────────────────
//
// The Stage-3 WMS regen takes 30-90s of awaits between the consent gate (which
// only saw the ENTRY-TIME scope snapshot) and the post-WMS scope writeback.
// A user who edited scope rows DURING that window never consented to losing
// those edits, but a plain setScope(aiScope) clobbered them.
//
// This merge carries forward any LIVE item the user manually edited *during the
// window* — `manuallyEdited === true` AND its client-facing copy differs from
// the entry snapshot — so it wins over the AI-derived version for the same key.
// Brand-new user rows (no entry-snapshot counterpart, or no merge key) are
// appended. Items the user had already edited BEFORE entry (unchanged during
// the window) are NOT preserved here — the consent dialog covered those.
//
// Mirrors applyMorphToScope's partition (scope-morph.js:749). PURE: no React,
// no mutation of inputs. Returns a NEW array. NEVER recomputes pricing/amounts
// (preserves the user item verbatim).
const wmsCopyFingerprint = (it) => JSON.stringify({
    t: String(it?.title || ''),
    d: String(it?.description || ''),
    b: String(it?.descriptionBrief || ''),
    n: String(it?.notes_to_client || ''),
    i: Array.isArray(it?.inclusions)
        ? it.inclusions.map(inc => (typeof inc === 'string' ? inc : String(inc?.text || ''))).join('')
        : '',
});

export function mergeWmsWritebackPreservingEdits(prevScope, aiScope, entrySnapshot, options = {}) {
    const ai = Array.isArray(aiScope) ? aiScope : [];
    const liveScopeWasProvided = Array.isArray(prevScope);
    const prevItems = Array.isArray(prevScope) ? prevScope : [];
    const snapshotItems = Array.isArray(entrySnapshot) ? entrySnapshot : [];

    const entryByKey = new Map();
    const snapshotIndexByItem = new Map();
    snapshotItems.forEach((it, index) => {
        snapshotIndexByItem.set(it, index);
        scopeMergeKeys(it).forEach((k) => { if (!entryByKey.has(k)) entryByKey.set(k, it); });
    });

    const liveSnapshotOrder = prevItems
        .map((item) => {
            const matched = scopeMergeKeys(item).map((key) => entryByKey.get(key)).find(Boolean) || null;
            return matched ? snapshotIndexByItem.get(matched) : null;
        })
        .filter((index) => Number.isInteger(index));
    const liveTopologyStillMatchesEntry = (
        new Set(liveSnapshotOrder).size === snapshotItems.length
        && liveSnapshotOrder.every((snapshotIndex, position) => snapshotIndex === position)
    );

    // A builder may delete or reorder Stage 4 rows during the 30–90 second WMS
    // call. That live topology is newer authority than the entry snapshot. In
    // this branch the live row set/order owns the result while fresh WMS fields
    // are still applied to one-to-one identity matches. AI-only rows are not
    // appended because an unmatched AI row may be the very entry row the
    // builder deleted; fail-safe preservation is preferable to resurrection.
    if (options?.entryTopologyWasLive !== false && liveScopeWasProvided && !liveTopologyStillMatchesEntry) {
        const aiByKey = new Map();
        ai.forEach((item) => {
            scopeMergeKeys(item).forEach((key) => {
                const matches = aiByKey.get(key) || [];
                matches.push(item);
                aiByKey.set(key, matches);
            });
        });
        const consumedAi = new Set();
        const mergedLiveTopology = prevItems.map((liveItem) => {
            const keys = scopeMergeKeys(liveItem);
            const snap = keys.map((key) => entryByKey.get(key)).find(Boolean) || null;
            const aiItem = keys
                .flatMap((key) => aiByKey.get(key) || [])
                .find((candidate) => !consumedAi.has(candidate)) || null;
            if (!aiItem) return liveItem;
            consumedAi.add(aiItem);
            const preserveCopy = liveItem?.manuallyEdited === true
                && (!snap || wmsCopyFingerprint(liveItem) !== wmsCopyFingerprint(snap));
            const preserveAmount = hasReviewableManualAmountAuthority(liveItem);
            return refreshQuotedAmountsFromLockedAmount({
                ...aiItem,
                ...(preserveCopy ? copyAuthorityFields(liveItem) : {}),
                ...(preserveAmount ? scopeAmountAuthorityFields(liveItem) : {}),
            });
        });
        const topologyDelta = Math.max(
            1,
            Math.abs(prevItems.length - snapshotItems.length),
            prevItems.reduce((count, item, index) => {
                const entryKeys = new Set(scopeMergeKeys(snapshotItems[index]));
                return count + (scopeMergeKeys(item).some((key) => entryKeys.has(key)) ? 0 : 1);
            }, 0),
        );
        return {
            scope: mirrorScopeDescriptions(mergedLiveTopology),
            preservedCount: topologyDelta,
        };
    }

    // Find live items the user edited DURING the AI window.
    const preservedByKey = new Map();
    const preservedEntries = [];
    const preservedExtras = [];
    for (const p of prevItems) {
        const keys = scopeMergeKeys(p);
        const snap = keys.map(k => entryByKey.get(k)).find(Boolean) || null;
        const preserveCopy = p?.manuallyEdited === true
            && (!snap || wmsCopyFingerprint(p) !== wmsCopyFingerprint(snap));
        const preserveAmount = hasReviewableManualAmountAuthority(p);
        if (!preserveCopy && !preserveAmount) continue;
        const entry = {
            item: p,
            preserveCopy,
            preserveAmount,
            snap,
            snapshotIndex: snap ? snapshotIndexByItem.get(snap) : -1,
        };
        if (keys.length > 0) {
            keys.forEach(k => {
                const entries = preservedByKey.get(k) || [];
                if (!entries.includes(entry)) entries.push(entry);
                preservedByKey.set(k, entries);
            });
            preservedEntries.push(entry);
        } else preservedExtras.push(entry);
    }

    if (preservedByKey.size === 0 && preservedExtras.length === 0) {
        // Mirror the single Scope Description into the legacy `description` field
        // (fill-if-empty) — symmetric with mergeQuoteScopeWithLiveScope, so this
        // WMS-writeback boundary never emits an empty `description` either.
        return { scope: mirrorScopeDescriptions(ai), preservedCount: 0 };
    }

    const consumed = new Set();
    const assignedByAiIndex = new Map();
    ai.forEach((item, aiIndex) => {
        const hit = scopeMergeKeys(item)
            .flatMap(k => preservedByKey.get(k) || [])
            .find(entry => !consumed.has(entry));
        if (hit) {
            consumed.add(hit);
            assignedByAiIndex.set(aiIndex, hit);
        }
    });

    // A generator may legitimately re-key a row. Existing edited rows fall
    // forward to their original position when that slot is still available;
    // only truly new user rows are appended. This keeps the merge one-to-one
    // instead of duplicating the old row beside its regenerated replacement.
    for (const entry of preservedEntries) {
        if (consumed.has(entry) || !entry.snap) continue;
        const belongsToDifferentSnapshot = (aiIndex) => {
            const knownSnapshot = scopeMergeKeys(ai[aiIndex])
                .map(key => entryByKey.get(key))
                .find(Boolean) || null;
            return knownSnapshot && knownSnapshot !== entry.snap;
        };
        let targetIndex = Number.isInteger(entry.snapshotIndex)
            && entry.snapshotIndex >= 0
            && entry.snapshotIndex < ai.length
            && !assignedByAiIndex.has(entry.snapshotIndex)
            && !belongsToDifferentSnapshot(entry.snapshotIndex)
            ? entry.snapshotIndex
            : -1;
        if (targetIndex < 0) {
            const freeIndices = ai.map((_item, index) => index)
                .filter(index => !assignedByAiIndex.has(index) && !belongsToDifferentSnapshot(index));
            if (freeIndices.length === 1) targetIndex = freeIndices[0];
        }
        if (targetIndex >= 0) {
            consumed.add(entry);
            assignedByAiIndex.set(targetIndex, entry);
        }
    }

    const merged = ai.map((item, aiIndex) => {
        const hit = assignedByAiIndex.get(aiIndex);
        if (!hit) return item;
        return refreshQuotedAmountsFromLockedAmount({
            ...item,
            ...(hit.preserveCopy ? copyAuthorityFields(hit.item) : {}),
            ...(hit.preserveAmount ? scopeAmountAuthorityFields(hit.item) : {}),
        });
    });
    for (const entry of preservedEntries) if (!consumed.has(entry)) merged.push(entry.item);
    for (const entry of preservedExtras) merged.push(entry.item);
    const preservedCount = consumed.size
        + preservedEntries.filter(entry => !consumed.has(entry)).length
        + preservedExtras.length;
    return { scope: mirrorScopeDescriptions(merged), preservedCount };
}

export const _internals = {
    inclusionText,
    copyStampMs,
    pricingStampMs,
    scopeTopologyStampMs,
    isPreservableQuoteOnlyItem,
    shouldPreserveQuoteClientCopy,
    shouldPreserveQuotePricing,
    wmsCopyFingerprint,
};
